import { useEffect, useState } from "react";
import { ChevronLeft, ShieldCheck, XCircle, Search, Send } from "lucide-react";
import client, { extractError } from "@/api";

const STATUS_LABEL = {
  PENDING_FULFILMENT: "Awaiting fulfilment",
  FULFILMENT_CONFIRMED: "Awaiting settlement",
  SETTLEMENT_PENDING: "Settlement pending",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
  DISPUTED: "Disputed",
};

const SETTLEMENT_LABEL = {
  NOT_STARTED: "Not started",
  RESERVED: "Reserved",
  SETTLED: "Settled",
  FAILED: "Failed",
  REVERSED: "Released",
};

const FILTERS = [
  { value: "FULFILMENT_CONFIRMED", label: "Awaiting settle" },
  { value: "SETTLEMENT_PENDING", label: "Settlement pending" },
  { value: "PENDING_FULFILMENT", label: "Fulfilment" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "", label: "All" },
];

function CancelDialog({ onCancel, onConfirm, busy }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  return (
    <div className="dialog-backdrop" role="dialog" data-testid="txn-cancel-dialog">
      <form className="dialog-panel" onSubmit={(e) => { e.preventDefault(); onConfirm(reason, notes); }}>
        <h3>Cancel transaction</h3>
        <p className="muted small">Cancellation releases any reserved credit back to the buyer.</p>
        <label>Reason (visible to parties, minimum 5 characters)
          <textarea required minLength={5} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="txn-cancel-reason" />
        </label>
        <label>Internal admin notes (optional)
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="txn-cancel-notes" />
        </label>
        <div className="dialog-actions">
          <button type="button" className="ghost-button" onClick={onCancel}>Back</button>
          <button type="submit" className="danger-button-solid" disabled={busy} data-testid="txn-cancel-confirm">{busy ? "Working…" : "Confirm cancel"}</button>
        </div>
      </form>
    </div>
  );
}

export default function AdminTransactionsPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("FULFILMENT_CONFIRMED");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (filter) params.set("status", filter);
      if (search) params.set("search", search);
      params.set("page_size", 50);
      const r = await client.get(`/admin/transactions?${params.toString()}`);
      setItems(r.data.items || []); setCounts(r.data.counts || {});
    } catch (err) { setError(extractError(err, "Unable to load transactions.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openDetail = async (id) => {
    try { const r = await client.get(`/admin/transactions/${id}`); setDetail(r.data); setMessage(""); setError(""); }
    catch (err) { setError(extractError(err)); }
  };

  const act = async (path, ok, payload = {}) => {
    if (!detail) return;
    setBusy(true); setError(""); setMessage("");
    try {
      const r = await client.post(`/admin/transactions/${detail.id}/${path}`, payload);
      // Merge to preserve enriched fields (audit, ledger).
      setDetail((d) => ({ ...d, ...r.data }));
      setMessage(ok);
      load();
    } catch (err) { setError(extractError(err)); }
    finally { setBusy(false); }
  };

  const authorise = () => act("authorise-settlement", "Credit reserved. Ready to settle.");
  const settle = () => act("settle", "Settlement complete.");
  const cancel = (reason, notes) => act("cancel", "Transaction cancelled.", { reason, admin_notes: notes || null }).then(() => setDialog(null));

  if (detail) {
    return (
      <div className="admin-company-detail" data-testid="admin-transaction-detail">
        <button type="button" className="link-button back" data-testid="admin-transaction-back" onClick={() => setDetail(null)}><ChevronLeft size={14} /> Back to transactions</button>
        <header className="detail-header">
          <div>
            <p className="eyebrow">{STATUS_LABEL[detail.status]}</p>
            <h2>{detail.listing_title}</h2>
            <p className="muted">Buyer: {detail.buyer_name} ({detail.buyer_status}) · Seller: {detail.seller_name} ({detail.seller_status})</p>
          </div>
          <div className="chip-stack">
            <span className={`status-chip status-${detail.status.toLowerCase()}`}>{STATUS_LABEL[detail.status]}</span>
            <span className={`status-chip status-${detail.settlement_status.toLowerCase()}`}>Settlement: {SETTLEMENT_LABEL[detail.settlement_status]}</span>
          </div>
        </header>
        {error && <div className="form-error">{error}</div>}
        {message && <div className="form-success" data-testid="admin-txn-message">{message}</div>}
        <section className="detail-grid">
          <article><p className="eyebrow">TRADE VALUE</p><p><strong>₹{detail.trade_value.toLocaleString("en-IN")}</strong></p></article>
          <article><p className="eyebrow">FACILITATOR FEE ({detail.facilitator_fee_rate_percent}%)</p><p>₹{detail.facilitator_fee_amount.toLocaleString("en-IN")}</p></article>
          <article><p className="eyebrow">QUANTITY</p><p>{detail.quantity ?? "—"}</p></article>
          <article><p className="eyebrow">UNIT VALUE</p><p>₹{detail.unit_value.toLocaleString("en-IN")}</p></article>
          <article><p className="eyebrow">SETTLEMENT REF</p><p>{detail.settlement_reference || "—"}</p></article>
          <article><p className="eyebrow">TRADE REQUEST</p><p><small>{detail.trade_request?.id.slice(0, 8) || "—"}</small></p></article>
        </section>
        <section className="section-block">
          <p className="eyebrow">ACTIONS</p>
          <div className="button-row">
            {detail.status === "FULFILMENT_CONFIRMED" && detail.settlement_status === "NOT_STARTED" &&
              <button type="button" className="primary-button" onClick={authorise} disabled={busy} data-testid="txn-authorise-settlement"><ShieldCheck size={14} /> Reserve credit</button>}
            {detail.status === "SETTLEMENT_PENDING" && detail.settlement_status === "RESERVED" &&
              <button type="button" className="primary-button" onClick={settle} disabled={busy} data-testid="txn-settle"><Send size={14} /> Settle now</button>}
            {!["COMPLETED", "CANCELLED"].includes(detail.status) &&
              <button type="button" className="danger-button-solid" onClick={() => setDialog("cancel")} disabled={busy} data-testid="txn-cancel"><XCircle size={14} /> Cancel</button>}
          </div>
        </section>
        {detail.ledger_entries?.length > 0 && (
          <section className="documents-section">
            <div className="section-heading"><div><p className="eyebrow">LEDGER</p><h3>Immutable ledger entries</h3></div></div>
            <table className="admin-table" data-testid="txn-ledger">
              <thead><tr><th>Entry</th><th>Company</th><th>Credit</th><th>Debit</th><th>Balance after</th><th>Ref</th><th>When</th></tr></thead>
              <tbody>
                {detail.ledger_entries.map((le) => (
                  <tr key={le.id}>
                    <td><strong>{le.entry_type}</strong></td>
                    <td><small>{le.company_id.slice(0, 8)}</small></td>
                    <td>₹{le.credit_amount.toLocaleString("en-IN")}</td>
                    <td>₹{le.debit_amount.toLocaleString("en-IN")}</td>
                    <td>₹{le.balance_after.toLocaleString("en-IN")}</td>
                    <td><small>{le.reference_id}</small></td>
                    <td><small>{new Date(le.created_at).toLocaleString()}</small></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
        {detail.audit_log?.length > 0 && (
          <section className="documents-section">
            <div className="section-heading"><div><p className="eyebrow">HISTORY</p><h3>Audit trail</h3></div></div>
            <ul className="audit-list" data-testid="admin-txn-audit">
              {detail.audit_log.map((a) => (
                <li key={a.id}>
                  <strong>{a.event}</strong><small>{a.previous_state || "—"} → {a.new_state || "—"}</small>
                  {a.notes && <p className="muted small">{a.notes}</p>}
                  <small className="muted">{a.actor_email || "system"} · {a.actor_role} · {new Date(a.created_at).toLocaleString()}</small>
                </li>
              ))}
            </ul>
          </section>
        )}
        {dialog === "cancel" && <CancelDialog onCancel={() => setDialog(null)} onConfirm={cancel} busy={busy} />}
      </div>
    );
  }

  return (
    <div className="admin-listings-page" data-testid="admin-transactions-page">
      <div className="admin-toolbar">
        <div className="filter-tabs" data-testid="admin-txn-tabs">
          {FILTERS.map((o) => (
            <button key={o.value || "all"} type="button" data-testid={`admin-txn-filter-${o.value || "all"}`} className={filter === o.value ? "active" : ""} onClick={() => setFilter(o.value)}>
              {o.label}{o.value && counts[o.value] > 0 && <span className="count">{counts[o.value]}</span>}
            </button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="search-row">
          <input placeholder="Search listing title or txn id" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-txn-search" />
          <button type="submit" className="ghost-button"><Search size={12} /></button>
        </form>
      </div>
      {error && <div className="form-error">{error}</div>}
      {loading ? <div className="loading-block">Loading…</div> : (
        <table className="admin-table" data-testid="admin-transactions-table">
          <thead><tr><th>Listing</th><th>Buyer</th><th>Seller</th><th>Trade value</th><th>Fee</th><th>Fulfilment</th><th>Settlement</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="empty-row">No transactions in this view.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} data-testid={`admin-txn-row-${r.id}`}>
                <td><strong>{r.listing_title}</strong><br /><small className="muted">{r.id.slice(0, 8)}</small></td>
                <td>{r.buyer_name}<br /><small className="muted">{r.buyer_status}</small></td>
                <td>{r.seller_name}<br /><small className="muted">{r.seller_status}</small></td>
                <td>₹{r.trade_value.toLocaleString("en-IN")}</td>
                <td>₹{r.facilitator_fee_amount.toLocaleString("en-IN")}</td>
                <td><small>{r.fulfilment_status}</small></td>
                <td><small>{SETTLEMENT_LABEL[r.settlement_status]}</small></td>
                <td><span className={`status-chip status-${r.status.toLowerCase()}`}>{STATUS_LABEL[r.status]}</span></td>
                <td><button type="button" className="link-button" data-testid={`admin-open-txn-${r.id}`} onClick={() => openDetail(r.id)}>Review →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
